import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getUsers } from '../../api/endpoints/admin';
import type { AdminUserResponse, UserRole } from '../../api/types';
import { StatusChip } from './StatusChip';
import styles from './Payments.module.css';
import { Button } from '../../components/ui/Button';
import { DialogHelp } from '../../help/DialogHelp';

function PickerRow({
  user,
  role,
  onSelect,
}: {
  user: AdminUserResponse;
  role: UserRole;
  onSelect: (user: AdminUserResponse) => void;
}) {
  const { t } = useTranslation();
  return (
    <Button
      variant="secondary"
      className={styles.pickerRow}
      size="sm"
      onClick={() => onSelect(user)}
    >
      <strong>{t('payments.counterpartyPicker.contact', { name: user.fullName ?? '—' })}</strong>
      <span>{user.phoneNumber}</span>
      <span>
        <StatusChip displayStatus={`KYC_${user.kycStatus}`} />
      </span>
      {role === 'FLEET_MANAGER' && <span>{t('payments.counterpartyPicker.bankKycNote')}</span>}
    </Button>
  );
}

export function CounterpartyPicker({
  role,
  onSelect,
  onClose,
}: {
  role: UserRole;
  onSelect: (user: AdminUserResponse) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);
  const query = useQuery({
    queryKey: ['admin', 'users', role, 'payments-picker', debouncedSearch, page],
    queryFn: () => getUsers({ role, search: debouncedSearch || undefined, size: 8, page }),
    staleTime: 30_000,
  });
  return (
    <div className={styles.scrim}>
      <section
        role="dialog"
        aria-modal="true"
        aria-label={role === 'SHIPPER' ? 'Choose shipper' : 'Choose partner'}
        className={`${styles.dialog} ${styles.pickerDialog}`}
      >
        <header className={styles.header}>
          <h2>{role === 'SHIPPER' ? 'Choose shipper' : 'Partner'}</h2>
          <div className={styles.actions}>
            <DialogHelp
              context={role === 'SHIPPER' ? 'choose-shipper' : 'choose-partner'}
              title={role === 'SHIPPER' ? 'Choose shipper' : 'Choose partner'}
            />
            <Button variant="secondary" size="sm" onClick={onClose}>
              {t('common.actions.cancel')}
            </Button>
          </div>
        </header>
        <div className={styles.body}>
          <label className={styles.field}>
            {t('payments.counterpartyPicker.searchLabel')}
            <input
              autoFocus
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
            />
          </label>
        </div>
        <div className={styles.pickerResults}>
          {query.isLoading ? (
            <p>{t('payments.counterpartyPicker.searching')}</p>
          ) : query.isError ? (
            <button onClick={() => void query.refetch()}>{t('payments.counterpartyPicker.retrySearch')}</button>
          ) : (
            query.data?.items.map((user) => (
              <PickerRow key={user.id} role={role} user={user} onSelect={onSelect} />
            ))
          )}
        </div>
        <div className={styles.body}>
          <div className={styles.actions}>
            <Button
              variant="secondary"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
            >
              {t('payments.counterpartyPicker.previous')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={!query.data || query.data.items.length < 8}
              onClick={() => setPage(page + 1)}
            >
              {t('payments.counterpartyPicker.next')}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
